import {
    type MoneyMapRuleGroup,
    MoneyMapRuleType,
    type MoneyMapRuleWithAccount,
    type MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { atom } from 'jotai';

export const editableMoneyMapAtom = atom<MoneyMapWithRulesAndAccounts | null>(
    null,
);

export const selectedMoneyMapRuleAtom = atom<MoneyMapRuleWithAccount | null>(
    null,
);

export type MoneyMapRulePatch = Partial<
    Pick<
        MoneyMapRuleWithAccount,
        | 'description'
        | 'percentage'
        | 'targetBalanceCents'
        | 'monthlyContributionMaxCents'
        | 'yearlyContributionMaxCents'
        | 'fixedAmountCents'
        | 'routingRuleType'
        | 'goalType'
    >
>;

export const updateRuleInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    ruleId: string,
    patch: MoneyMapRulePatch,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRules: moneyMap.moneyMapRules.map((rule) =>
            rule.id === ruleId ? { ...rule, ...patch } : rule,
        ),
    };
};

export const addRuleGroupInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    newRuleGroup: MoneyMapRuleGroup,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRuleGroups: [...moneyMap.moneyMapRuleGroups, newRuleGroup],
    };
};

export const addRuleInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    newRule: MoneyMapRuleWithAccount,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRules: [...moneyMap.moneyMapRules, newRule],
    };
};

export const deleteRuleInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    ruleId: string,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRules: moneyMap.moneyMapRules.filter(
            (rule) => rule.id !== ruleId,
        ),
    };
};

export const updateRuleGroupInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    groupId: string,
    patch: Partial<Pick<MoneyMapRuleGroup, 'name' | 'percentage'>>,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRuleGroups: moneyMap.moneyMapRuleGroups.map((group) =>
            group.id === groupId ? { ...group, ...patch } : group,
        ),
    };
};

export const deleteRuleSetInMoneyMap = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    groupId: string,
): MoneyMapWithRulesAndAccounts => {
    return {
        ...moneyMap,
        moneyMapRuleGroups: moneyMap.moneyMapRuleGroups.filter(
            (group) => group.id !== groupId,
        ),
        moneyMapRules: moneyMap.moneyMapRules.filter(
            (rule) => rule.moneyMapRuleGroupId !== groupId,
        ),
    };
};

export const reorderRuleSets = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    reorderedGroups: MoneyMapRuleGroup[],
): MoneyMapWithRulesAndAccounts => {
    // Priorities are shared across bills, spend and goals, so goal groups start
    // after the bills/spend rules (see getSyntheticGoalPriorityFromActual).
    const numNonGoalRules = moneyMap.moneyMapRules.filter(
        (rule) =>
            rule.type === MoneyMapRuleType.BILLS ||
            rule.type === MoneyMapRuleType.SPEND,
    ).length;

    return {
        ...moneyMap,
        moneyMapRuleGroups: reorderedGroups.map((group, index) => ({
            ...group,
            priority: numNonGoalRules + index + 1,
        })),
    };
};

/**
 * Reorders the rule groups to match `orderedSetIds`. Ids that aren't real
 * groups (the synthetic monthly expenses / ungrouped sets) are skipped.
 */
export const reorderRuleSetsById = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    orderedSetIds: string[],
): MoneyMapWithRulesAndAccounts => {
    const groupsById = new Map(
        moneyMap.moneyMapRuleGroups.map((group) => [group.id, group]),
    );
    return reorderRuleSets(
        moneyMap,
        orderedSetIds.flatMap((setId) => {
            const group = groupsById.get(setId);
            return group != null ? [group] : [];
        }),
    );
};

export const reorderRulesWithinSet = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    reorderedRuleIds: string[],
): MoneyMapWithRulesAndAccounts => {
    const priorityMap = new Map(
        reorderedRuleIds.map((id, index) => [id, index + 1]),
    );

    return {
        ...moneyMap,
        moneyMapRules: moneyMap.moneyMapRules.map((rule) => {
            const newPriority = priorityMap.get(rule.id);
            if (newPriority != null) {
                return { ...rule, priority: newPriority };
            }
            return rule;
        }),
    };
};

export const reorderBillsAndSpendRules = (
    moneyMap: MoneyMapWithRulesAndAccounts,
    reorderedRuleIds: string[],
): MoneyMapWithRulesAndAccounts => {
    return reorderRulesWithinSet(moneyMap, reorderedRuleIds);
};
